import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_IMAGE =
    "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/judl7qlx_Infused%20with%20Himalayan%20Rhubarb%20Unscented%20Tallow%20soap.jpg";
const INFUSION_IMAGE =
    "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/qc8xdqm5_Infused%20with%20Himalayan%20Rhubarb%20Cinnamon%20and%20clove%20Tallow%20soap.jpg";
const TALLOW_IMAGE =
    "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/91f4csle_Pure%20Tallow%20soap.jpg";
const SALT_IMAGE =
    "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/u7skbuj9_Alati%20signature%20salt%20bar%20tallow%20soap%20infused%20with%20Indigo%20powder.jpg";

const Home = () => {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        getProducts().then(setProducts).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section data-testid="hero-section" className="relative -mt-20 pt-20 bg-[#FAF9F6]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center pt-12 lg:pt-20 pb-24">
                    <div className="lg:col-span-5 fade-up">
                        <p className="label">Traditional Handcrafted Tallow Soap</p>
                        <h1
                            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6 text-[#1C1C1A]"
                            data-testid="hero-headline"
                        >
                            Soap made
                            <br />
                            properly.
                        </h1>
                        <p
                            className="mt-6 text-lg uppercase tracking-[0.2em] text-[#6A7059]"
                            data-testid="hero-subline"
                        >
                            Made the old way. Trusted today.
                        </p>
                        <p className="mt-8 text-lg text-[#4A4A46] leading-relaxed max-w-md">
                            Handcrafted in Darwin using traditional soap-making methods,
                            thoughtfully chosen ingredients, and recipes inspired by
                            generations of Greek family tradition.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link to="/shop" className="btn-primary" data-testid="hero-shop-cta">
                                Shop Soap
                            </Link>
                            <Link to="/our-story" className="btn-secondary" data-testid="hero-story-cta">
                                Our Story
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-7 fade-up">
                        <div className="product-image-wrap aspect-[5/6] lg:aspect-[6/7]">
                            <img
                                src={HERO_IMAGE}
                                alt="ALATI Himalayan Rhubarb soap bar"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY */}
            <section data-testid="philosophy-section" className="py-24 lg:py-32 px-6 lg:px-10 bg-[#FAF9F6]">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="label">A family belief</p>
                    <blockquote
                        className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-[#1C1C1A] mt-8"
                        data-testid="adonis-quote"
                    >
                        &ldquo;It&rsquo;s not just what goes in your body, it&rsquo;s what
                        goes on your body too.&rdquo;
                    </blockquote>
                    <p className="mt-6 text-sm uppercase tracking-[0.22em] text-[#737562]">
                        &mdash; Adonis
                    </p>
                    <p className="mt-12 max-w-2xl mx-auto text-[#4A4A46] leading-relaxed">
                        That simple sentence became part of ALATI. Not as a slogan. As a
                        family belief.
                    </p>
                </div>
            </section>

            {/* MEET THE BARS */}
            <section data-testid="meet-the-bars-section" className="py-24 px-6 lg:px-10 bg-[#FAF9F6]">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <p className="label">Meet the Bars</p>
                            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[#1C1C1A]">
                                Our first release.
                            </h2>
                            <p className="mt-5 font-serif italic text-lg text-[#1C1C1A] max-w-md leading-snug">
                                Every bar begins the same way.
                                <br />
                                The story changes with the ingredients.
                            </p>
                        </div>
                        <Link
                            to="/shop"
                            className="hidden sm:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] alati-link"
                            data-testid="meet-bars-view-all"
                        >
                            View All <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div
                        className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12"
                        data-testid="meet-bars-grid"
                    >
                        {products.slice(0, 4).map((p) => (
                            <ProductCard key={p.slug} product={p} />
                        ))}
                    </div>
                </div>
            </section>

            {/* FROM PLANT TO BAR PREVIEW */}
            <section data-testid="from-plant-section" className="py-24 lg:py-32 px-6 lg:px-10 bg-[#F0EBE1]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-6">
                        <div className="product-image-wrap aspect-[4/5]">
                            <img
                                src={INFUSION_IMAGE}
                                alt="Slow botanical infusion — Himalayan rhubarb soap"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <p className="label">From Plant to Bar</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#1C1C1A] leading-tight">
                            We start with the oil
                            <br />
                            and move outward.
                        </h2>
                        <p className="mt-6 text-[#4A4A46] leading-relaxed">
                            Before many of our soaps are ever poured into a mould, the oils
                            begin another journey. Botanicals are slowly infused into olive oil
                            and other selected oils before they become part of the soap.
                        </p>
                        <p className="mt-4 text-[#4A4A46] leading-relaxed italic">
                            Nature creates the colour. We provide the patience.
                        </p>
                        <div className="mt-10">
                            <Link
                                to="/process"
                                className="btn-secondary"
                                data-testid="from-plant-cta"
                            >
                                See the Process
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY TRADITIONAL SOAP PREVIEW */}
            <section data-testid="tradition-section" className="py-24 lg:py-32 px-6 lg:px-10 bg-[#FAF9F6]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <p className="label">Why Traditional Soap?</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#1C1C1A] leading-tight">
                            Made properly.
                            <br />
                            Made the old way.
                        </h2>
                        <p className="mt-6 text-[#4A4A46] leading-relaxed">
                            ALATI specialises in handcrafted tallow soap. Made slowly, in
                            small batches, with grass-fed tallow and oils prepared by hand.
                            Good soap does not need to be complicated. It needs to be made
                            properly.
                        </p>
                        <div className="mt-10">
                            <Link
                                to="/tradition"
                                className="btn-secondary"
                                data-testid="tradition-cta"
                            >
                                Learn More
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-6 order-1 lg:order-2">
                        <div className="product-image-wrap aspect-[4/5]">
                            <img
                                src={TALLOW_IMAGE}
                                alt="Pure Tallow Soap — handcrafted in Darwin"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* KALYMNOS TO DARWIN */}
            <section data-testid="kalymnos-section" className="py-24 lg:py-32 px-6 lg:px-10 bg-[#FAF9F6] border-t border-[#E3DED5]">
                <div className="max-w-5xl mx-auto text-center">
                    <p className="label">Kalymnos to Darwin</p>
                    <h2
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.1] text-[#1C1C1A]"
                        data-testid="kalymnos-heading"
                    >
                        The story began in Greece.
                        <br />
                        The next chapter is
                        <br />
                        written in Darwin.
                    </h2>
                    <p className="mt-10 max-w-2xl mx-auto text-[#4A4A46] leading-relaxed">
                        Recipes shaped by generations of Greek family soap making on
                        Kalymnos. Handcrafted today in Darwin, Australia.
                    </p>
                    <div className="mt-12 product-image-wrap aspect-[16/9] max-w-3xl mx-auto">
                        <img
                            src={SALT_IMAGE}
                            alt="ALATI Signature Salt Bar — handcrafted in Darwin"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
